import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Form } from 'react-router'
import { Logo } from '#app/components/common/Logo'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { Separator } from '#app/components/ui/separator'
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetTrigger,
} from '#app/components/ui/sheet'
import { emitAnalyticsEvent, setSignupSource } from '#app/utils/pz/analytics'
import { buildCategoryTree } from '#app/utils/pz/navigation/categoryTree'
import { type NavigationItem } from '#app/utils/pz/navigation/resolveNavigation'
import { path, publicRoutes } from '#app/utils/pz/routes'
import { useOptionalUser } from '#app/utils/user'
import { type MarketingNavCategory } from './nav-menu'

type NavigationSheetProps = {
	categories?: MarketingNavCategory[]
	marketingLinks?: NavigationItem[]
	signupHref?: string
	signInHref?: string
	trigger?: React.ReactNode
}

export const NavigationSheet = ({
	categories,
	marketingLinks,
	signupHref,
	signInHref,
	trigger,
}: NavigationSheetProps) => {
	const { t } = useTranslation()
	const user = useOptionalUser()
	const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null)
	const resolvedSignupHref = signupHref ?? path('auth.signup')
	const resolvedSignInHref = signInHref ?? path('auth.login')
	const secondaryLinks = (marketingLinks ?? []).filter(
		(item) => item.href !== publicRoutes.courses.list(),
	)
	const handleSignupClick = () => {
		setSignupSource('hero_cta')
		emitAnalyticsEvent({
			name: 'signup_started',
			properties: { source: 'hero_cta' },
		})
	}

	const { rootCategories, childCategoriesByParentId } = useMemo(() => {
		return buildCategoryTree(categories)
	}, [categories])

	return (
		<Sheet>
			<SheetTrigger asChild>
				{trigger ?? (
					<Button variant="outline" size="icon" aria-label={t('common.toggle')}>
						{t('common.toggle')}
					</Button>
				)}
			</SheetTrigger>
			<SheetContent side="left" className="flex h-dvh flex-col p-0">
				<div className="border-b px-4 py-4">
					<div className="flex justify-center">
						<Logo imgClassName="w-28" />
					</div>
				</div>

				<div className="flex-1 overflow-y-auto px-4 py-4">
					{activeCategoryId ? (
						<>
							<div className="flex items-center gap-2">
								<Button
									variant="ghost"
									size="icon"
									onClick={() => setActiveCategoryId(null)}
									aria-label={t('common.actions.back')}
								>
									<Icon name="arrow-left" size="sm" />
								</Button>
								{(() => {
									const cat = rootCategories.find(
										(c) => c.id === activeCategoryId,
									)
									if (!cat) return null
									return (
										<SheetClose asChild>
											<Link
												to={`${publicRoutes.courses.list()}?category=${cat.slug}`}
												className="hover:bg-accent block flex-1 rounded-md px-3 py-3 text-sm font-semibold transition-colors"
												reloadDocument
											>
												{cat.name}
											</Link>
										</SheetClose>
									)
								})()}
							</div>

							<div className="mt-2 space-y-1">
								{(() => {
									const cat = rootCategories.find(
										(c) => c.id === activeCategoryId,
									)
									if (!cat) return null
									return (
										<>
											<Separator />
											{(childCategoriesByParentId.get(cat.id) ?? []).map(
												(child) => (
													<SheetClose key={child.id} asChild>
														<Link
															to={`${publicRoutes.courses.list()}?subcategory=${child.slug}`}
															className="hover:bg-accent block rounded-md px-3 py-3 text-sm transition-colors"
															reloadDocument
														>
															{child.name}
														</Link>
													</SheetClose>
												),
											)}
										</>
									)
								})()}
							</div>
						</>
					) : (
						<>
							<div className="mb-4">
								<SheetClose asChild>
									<Link
										to={publicRoutes.catalog.index()}
										className="hover:bg-accent block rounded-md px-3 py-3 text-sm font-medium transition-colors"
										reloadDocument
									>
										{t('marketing.nav.catalog')}
									</Link>
								</SheetClose>
							</div>

							<div className="px-3 py-3 text-sm font-medium">
								{t('marketing.nav.learn')}
							</div>
							<div className="mt-1 space-y-1">
								{rootCategories.map((cat) => {
									const hasChildren =
										(childCategoriesByParentId.get(cat.id) ?? []).length > 0
									return hasChildren ? (
										<button
											key={cat.id}
											type="button"
											className="hover:bg-accent flex w-full items-center justify-between rounded-md px-3 py-3 text-left text-sm transition-colors"
											onClick={() => setActiveCategoryId(cat.id)}
										>
											<span>{cat.name}</span>
											<Icon
												name="chevron-right"
												size="sm"
												className="text-muted-foreground"
											/>
										</button>
									) : (
										<SheetClose key={cat.id} asChild>
											<Link
												to={`${publicRoutes.courses.list()}?category=${cat.slug}`}
												className="hover:bg-accent block rounded-md px-3 py-3 text-sm transition-colors"
												reloadDocument
											>
												{cat.name}
											</Link>
										</SheetClose>
									)
								})}
							</div>

							{secondaryLinks.length > 0 ? (
								<div className="mt-4 space-y-1">
									<Separator />
									{secondaryLinks.map((item) => (
										<SheetClose key={item.id} asChild>
											{item.target || item.href.startsWith('http') ? (
												<a
													href={item.href}
													target={item.target}
													rel={
														item.target === '_blank' ? 'noreferrer' : undefined
													}
													className="hover:bg-accent block rounded-md px-3 py-3 text-sm transition-colors"
												>
													{item.name}
												</a>
											) : (
												<Link
													to={item.href}
													className="hover:bg-accent block rounded-md px-3 py-3 text-sm transition-colors"
												>
													{item.name}
												</Link>
											)}
										</SheetClose>
									))}
								</div>
							) : null}
						</>
					)}

					<div className="mt-6">
						<Separator />
						{user ? (
							<div className="space-y-1 pt-4">
								<SheetClose asChild>
									<Link
										to="/learn/dashboard"
										className="hover:bg-accent block rounded-md px-3 py-3 text-sm transition-colors"
									>
										{t('navigation.sidebar.studyPanel')}
									</Link>
								</SheetClose>
								<SheetClose asChild>
									<Link
										to={path('settings.billing')}
										className="hover:bg-accent block rounded-md px-3 py-3 text-sm transition-colors"
									>
										{t('navigation.sidebar.settings')}
									</Link>
								</SheetClose>
								<Form
									action={path('auth.logout')}
									method="POST"
									className="w-full"
								>
									<Button
										type="submit"
										variant="ghost"
										className="text-destructive hover:text-destructive w-full justify-start px-3"
									>
										{t('navigation.sidebar.logout.title')}
									</Button>
								</Form>
							</div>
						) : (
							<div className="space-y-2 pt-4">
								<Button variant="outline" className="w-full" asChild>
									<Link to={resolvedSignInHref}>
										{t('marketing.nav.signIn')}
									</Link>
								</Button>
								<Button className="w-full" asChild>
									<Link to={resolvedSignupHref} onClick={handleSignupClick}>
										{t('marketing.nav.getStarted')}
									</Link>
								</Button>
							</div>
						)}
					</div>
				</div>
			</SheetContent>
		</Sheet>
	)
}
